<!-- 单文件上传 -->
<template>
  <el-upload
    :file-list="fileList"
    :accept="props.accept || '.jpg, .jpeg, .png, .gif, .JPG, .JPEG, .PNG, .GIF, .BMP'"
    :limit="props.limit"
    :drag="props.drag"
    :show-file-list="showFileList"
    :headers="headers"
    :action="UPLOAD_BASE_URL + '/manager/upload'"
    :before-upload="handleBeforeUpload"
    :on-exceed="handleOnExceed"
    list-type="picture-card" 
    name="file"
    :on-remove="handleRemove"
    :on-success="handleUploadSuccess"
    :class="{ limitActive: fileList.length && props.limit === 1 }"
  >
    <slot>
      <el-icon>
        <Plus /> <!-- 修复图标组件引用 -->
      </el-icon>
    </slot>
    <template v-if="showTips" #tip>
      <div class="el-upload__tip">
        {{ props.tips }} <!-- 默认提示补充大小限制 -->
      </div>
    </template>
  </el-upload>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { UPLOAD_BASE_URL } from '@/common/constant'
import { ElMessage } from 'element-plus' // 引入Element提示组件
import { Plus } from '@element-plus/icons-vue' // 引入正确的Plus图标

const emit = defineEmits(["update:modelValue"]);

const props = defineProps({
  /**
   * 文件路径集合（注：原类型为String，实际存URL字符串，保持原设计）
   */
  modelValue: {
    type: String,
    default: '',
  },
  /**
   * 文件上传数量限制
   */
  limit: {
    type: Number,
    default: 1, // 单文件上传默认限制1个，符合"单文件"场景
  },
  // 上传格式（优先级高于默认）
  accept: {
    type: String,
    default: null,
  },
  // 上传提示（可自定义，默认包含大小限制）
  tips: {
    type: String,
    default: '',
  },
  // 是否是拖拽上传
  drag: {
    type: Boolean,
    default: false,
  },
  // 是否显示上传列表
  showFileList: {
    type: Boolean,
    default: true,
  },
  // 是否显示提示
  showTips: {
    type: Boolean,
    default: true,
  },
  // 上传按钮名称（slot优先级更高，预留）
  btnText: {
    type: String,
    default: '上传文件',
  },
  // 最大上传大小（KB），默认50KB，支持外部自定义
  maxSize: {
    type: Number,
    default: 50,
  },
});

const fileList = ref([]);

// 监听modelValue变化，同步文件列表（修复原逻辑数组处理问题）
watch(() => props.modelValue, (newVal) => {
  fileList.value = []; // 清空原有列表
  if (!newVal) return; // 无URL时直接返回
  
  // 解析URL获取文件名（兼容不同路径格式）
  const url = newVal.trim();
  const fileName = url.split('/').pop() || '未知文件';
  fileList.value = [{ url, name: fileName }];
}, {
  deep: true,
  immediate: true,
});

// 新增：上传前校验（核心：50KB大小限制）
const handleBeforeUpload = (file) => {
  // 1. 计算文件大小（字节转KB：1KB=1024B）
  const fileSizeKB = file.size / 1024;
  
  // 2. 大小校验：超过maxSize（默认50KB）阻止上传
  if (fileSizeKB > props.maxSize) {
    ElMessage.error(`文件大小超出限制！最大支持${props.maxSize}KB，当前文件${Math.round(fileSizeKB)}KB`);
    return false; // 返回false阻止上传
  }

  // 3. 格式二次校验（避免绕过accept限制）
  const allowedExts = ['jpg', 'jpeg', 'png', 'gif', 'bmp'];
  const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
  if (!allowedExts.includes(fileExt)) {
    ElMessage.error('仅支持jpg、jpeg、png、gif、bmp格式文件');
    return false;
  }

  return true; // 校验通过，允许上传
};

// 新增：超出文件数量限制提示
const handleOnExceed = (files, uploadFiles) => {
  ElMessage.warning(`最多只能上传${props.limit}个文件`);
};

// 上传成功回调（修复原fileList赋值错误：数组赋值改为对象）
const handleUploadSuccess = (response) => {
  if (!response?.data?.fileName) { // 判空，避免接口返回异常
    ElMessage.error('文件上传失败，请重试');
    return;
  }
  
  const url = decodeURIComponent(response.data.fileName);
  const fileName = url.split('/').pop() || '上传文件';
  fileList.value = [{ url, name: fileName }]; // 单文件列表，赋值为数组
  emit("update:modelValue", url); // 向父组件传递文件URL
};

// 删除文件回调
const handleRemove = (removeFile, uploadFiles) => {
  fileList.value = [];
  emit("update:modelValue", ''); // 清空父组件绑定值
};
</script>

<style scoped lang="less">
/* 拖拽上传容器宽度 */
&:deep(.el-upload.is-drag) {
  width: 600px;
}

.limitActive {
  &:deep(.el-upload--picture-card) {
    display: none !important;
  }
}

</style>